<script>
/**
 * BaseValidationForm — wrapper de `<v-form>` que:
 *   1. Valida via Vuetify (rules nativos, mensagens inline)
 *   2. Ao falhar, foca automaticamente o primeiro campo invalido em ordem
 *      do DOM. Pra inputs Vuetify nativos (v-text-field, v-select, etc.)
 *      isso eh `.focus()` no DOM input. Pra componentes customizados,
 *      basta o componente expor metodo `focus()` proprio (ex: o picker
 *      abre o menu).
 *
 * Como componentes customizados participam:
 *   - Injetam `{ form: { default: null } }`, registram no `created()`
 *     via `this.form.register(this)`, expoem `valid`, `errorBucket` e
 *     metodo `validate()`. Mesma API publica que Vuetify usa internamente
 *     pra v-input — qualquer componente com essa interface eh validado
 *     pelo v-form sem mudanca extra.
 *
 * Uso:
 *   <BaseValidationForm ref="form">
 *     <InputByType :rules="[rules.required]" v-model="title" />
 *     <TaskSprintPicker :rules="[rules.required]" v-model="sprint" />
 *   </BaseValidationForm>
 *
 *   // No save():
 *   if (!this.$refs.form.validate()) return
 *   // ...prossegue
 */
export default {
  name: 'BaseValidationForm',
  inheritAttrs: false,
  methods: {
    validate() {
      const vform = this.$refs.vform
      if (!vform) return true
      const valid = vform.validate()
      if (!valid) this._focusFirstInvalid()
      return valid
    },
    resetValidation() {
      const vform = this.$refs.vform
      if (vform) vform.resetValidation()
    },
    reset() {
      const vform = this.$refs.vform
      if (vform) vform.reset()
    },
    _focusFirstInvalid() {
      const vform = this.$refs.vform
      if (!vform || !Array.isArray(vform.inputs)) return
      // vform.inputs vem na ordem em que os inputs se registraram — que
      // bate com a ordem do template/DOM. Pegar o primeiro com erro.
      const firstInvalid = vform.inputs.find(
        (input) =>
          input &&
          Array.isArray(input.errorBucket) &&
          input.errorBucket.length > 0
      )
      if (!firstInvalid) return
      this.$nextTick(() => {
        // 1) Scroll suave ate o input. Cobre 2 cenarios que o foco DOM
        //    nativo nao resolve bem:
        //      - lista longa com input fora do viewport (browser default
        //        faz jump abrupto; smooth eh melhor UX)
        //      - picker custom que "foca" abrindo um menu, sem cursor
        //        no DOM input — o browser nao detecta foco e nao rola.
        //    `block: 'center'` evita grudar no topo/bottom; o input
        //    aparece no meio da tela com contexto visivel ao redor.
        if (
          firstInvalid.$el &&
          typeof firstInvalid.$el.scrollIntoView === 'function'
        ) {
          firstInvalid.$el.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          })
        }
        // 2) Foco efetivo. Componentes Vuetify nativos tem `.focus()`
        //    herdado; componentes customizados precisam expor a propria
        //    (ex: TaskSprintPicker abre o menu).
        if (typeof firstInvalid.focus === 'function') {
          firstInvalid.focus()
        }
        // 3) Selects/autocompletes Vuetify expoem `activateMenu()` que
        //    abre o dropdown — `focus()` deles soh ativa o input por
        //    baixo, sem mostrar opcoes. Pra UX consistente com inputs
        //    de texto (cursor pisca convidando a digitar), aqui o menu
        //    abre convidando a escolher. Componentes que nao implementam
        //    sao ignorados (typeof check).
        if (typeof firstInvalid.activateMenu === 'function') {
          firstInvalid.activateMenu()
        }
      })
    },
  },
}
</script>

<template>
  <!-- @submit.prevent evita o reload nativo da página ao apertar Enter num input. -->
  <v-form ref="vform" v-bind="$attrs" v-on="$listeners" @submit.prevent>
    <slot />
  </v-form>
</template>
